import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Div, Flex, Gutter, Headline, Title } from '@gaiads/telia-react-component-library';
import { AdminCategory, AdminSubcategory, LocaleResponse } from '@cdaas/api-clients/dist/product-service-openapi';

import ProductCategories from './ProductCategories/ProductCategories';
import LocaleSettings from './LocaleSettings/LocaleSettings';
import Properties from './Properties/Properties';
import EditingLocale from '../../shared/EditingLocale/EditingLocale';
import Sections, { SectionsProps } from '../../shared/Sections/Sections';

import './AdminSettings.scss';

export interface AdminSettingsErrors {
  locales?: Record<LocaleResponse['id'], string>;
  categories?: Record<AdminCategory['id'], string>;
  subcategories?: Record<AdminSubcategory['id'], string>;
}
export interface AdminSettingsErrorProps {
  errors: AdminSettingsErrors;
  setErrors: React.Dispatch<React.SetStateAction<AdminSettingsErrors>>;
}
export default function AdminSettings() {
  const { t } = useTranslation('adminSettings');
  const [errors, setErrors] = useState<AdminSettingsErrors>({});
  const sectionTitles: SectionsProps['sectionTitles'] = [
    {
      id: 'locale-settings',
      title: t('localeSettings.title'),
      sectionHasErrors: Boolean(errors.locales),
    },
    {
      id: 'product-categories',
      title: t('productCategories.title'),
      sectionHasErrors: Boolean(errors.categories || errors.subcategories),
    },
    {
      id: 'properties',
      title: t('properties.title'),
      sectionHasErrors: false,
    },
  ];
  const sectionsRef = useRef<(HTMLDivElement | null)[]>(new Array(sectionTitles.length));

  const [isLocaleSettingsReady, setIsLocaleSettingsReady] = useState(false);
  const [isProductCategoriesReady, setIsProductCategoriesReady] = useState(false);
  const [isPropertiesReady, setIsPropertiesReady] = useState(false);
  const handlePropertiesReady = useCallback(() => setIsPropertiesReady(true), []);

  return (
    <Div margin={{ vertical: 'lg', horizontal: 'xxlg' }}>
      <Headline tagName="h1" size="md" margin={{ bottom: 'lg', left: 'sm' }}>
        {t('titles.adminSettings')}
      </Headline>
      <Flex pileVerticallyTo="sm" alignTop>
        <Flex className="admin-settings__content" padding={{ size: 'md' }} margin={{ size: 'sm' }} boxShadow="sm" borderRadius directionColumn>
          <div id="locale-settings" ref={element => (sectionsRef.current[0] = element)} />
          <Title tagName="h2" size="sm" margin={{ bottom: 'md' }}>
            {t('localeSettings.title')}
          </Title>
          <LocaleSettings setIsReady={setIsLocaleSettingsReady} />

          <Gutter />

          <div id="product-categories" ref={element => (sectionsRef.current[1] = element)} />
          <Title tagName="h2" size="sm" margin={{ bottom: 'md' }}>
            {t('productCategories.title')}
          </Title>
          <ProductCategories errors={errors} setErrors={setErrors} setIsReady={setIsProductCategoriesReady} />

          <Gutter />

          <div id="properties" ref={element => (sectionsRef.current[2] = element)} />
          <Title tagName="h2" size="sm" margin={{ bottom: 'md' }}>
            {t('properties.title')}
          </Title>
          <Properties onReady={handlePropertiesReady} />
        </Flex>
        <Flex className="admin-settings__panel" directionColumn>
          <EditingLocale
            accordionProps={{
              margin: { size: 'sm' },
            }}
          />
          <Sections
            sectionTitles={sectionTitles}
            sectionsRef={sectionsRef}
            scrollOnHash={isLocaleSettingsReady && isProductCategoriesReady && isPropertiesReady}
            accordionProps={{
              margin: { size: 'sm' },
            }}
          />
        </Flex>
      </Flex>
    </Div>
  );
}
